"use client"

import { ArrowUpRight01Icon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { AnimatePresence, motion } from "framer-motion"
import { useCallback, useEffect, useRef, useState } from "react"
import { SiTelegram, SiWhatsapp } from "react-icons/si"

import { Button } from "@/components/ui/button"
import { ANIMATION } from "@/lib/constants"
import { cn } from "@/lib/utils"

interface ContactButtonProps {
    className?: string
    contactText?: string
}

const ButtonWithIconDemo = ({
    className,
    contactText = "Let's Collaborate",
    onClick,
}: ContactButtonProps & { onClick?: () => void }) => {
    return (
        <Button
            type="button"
            variant="outline"
            onClick={onClick}
            className={cn(
                "group relative h-12 w-fit cursor-pointer overflow-hidden rounded-full p-1 ps-6 pe-14 font-medium text-sm transition-all duration-500 hover:ps-14 hover:pe-6",
                "border-border bg-background hover:border-foreground/20 dark:border-border dark:hover:border-foreground/30",
                className
            )}
        >
            <span className="relative z-10 transition-all duration-500">
                {contactText}
            </span>
            <div className="absolute right-1 flex h-10 w-10 items-center justify-center rounded-full bg-foreground text-background transition-all duration-500 group-hover:right-[calc(100%-44px)] group-hover:rotate-45">
                <HugeiconsIcon icon={ArrowUpRight01Icon} className="size-5" />
            </div>
        </Button>
    )
}

export function ContactButton({
    className,
    contactText = "Let's Collaborate",
    onActiveChange,
}: ContactButtonProps & { onActiveChange?: (active: boolean) => void }) {
    const [isActive, setIsActive] = useState(false)

    // Mirror the state outward so siblings (e.g. the hand-drawn annotation)
    // can react to the button swapping into its contact options. The callback
    // lives in a ref so `setActive` stays stable: an inline `onActiveChange`
    // would otherwise re-run the auto-dismiss effect below on every parent
    // render, restarting the 5s timer and pinning the options panel open.
    const onActiveChangeRef = useRef(onActiveChange)

    useEffect(() => {
        onActiveChangeRef.current = onActiveChange
    }, [onActiveChange])

    const setActive = useCallback((next: boolean) => {
        setIsActive(next)
        onActiveChangeRef.current?.(next)
    }, [])

    useEffect(() => {
        let timeoutId: NodeJS.Timeout

        if (isActive) {
            timeoutId = setTimeout(() => {
                setActive(false)
            }, 5000)
        }

        return () => clearTimeout(timeoutId)
    }, [isActive, setActive])

    return (
        <div
            className={cn(
                "relative z-50 flex min-h-12 items-center justify-center",
                className
            )}
        >
            <AnimatePresence mode="wait">
                {!isActive ? (
                    <motion.div
                        key="trigger"
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: ANIMATION.duration.normal }}
                        className="flex justify-center"
                    >
                        <ButtonWithIconDemo
                            contactText={contactText}
                            onClick={() => setActive(true)}
                        />
                    </motion.div>
                ) : (
                    <motion.div
                        key="options"
                        layout
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: ANIMATION.duration.normal }}
                        className="flex items-center gap-3"
                    >
                        <a
                            href="https://t.me/phalla404"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 transition-all hover:scale-105 hover:border-[#24A1DE] hover:bg-[#24A1DE] hover:text-white dark:border-border"
                        >
                            <SiTelegram
                                className="size-4 text-[#24A1DE] transition-colors group-hover:text-white"
                                aria-hidden="true"
                            />
                            <span className="font-medium text-sm">
                                Telegram
                            </span>
                        </a>
                        <a
                            href="https://wa.me/+855964407223"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 transition-all hover:scale-105 hover:border-[#25D366] hover:bg-[#25D366] hover:text-white dark:border-border"
                        >
                            <SiWhatsapp
                                className="size-4 text-[#25D366] transition-colors group-hover:text-white"
                                aria-hidden="true"
                            />
                            <span className="font-medium text-sm">
                                WhatsApp
                            </span>
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export { ButtonWithIconDemo }
